import React, { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";
import {
  getState,
  notifyRouteChange,
  start,
  subscribe,
} from "../../utils/appUpdateWatcher";
import {
  getDockReservedRight,
  subscribeDockReservedRight,
} from "../../utils/workspaceDock";
import styles from "./AppUpdatePrompt.module.css";

const SNOOZE_DURATION_MS = 30 * 60 * 1000;
// T-050 (F3) — breathing room between the prompt and whatever it has to clear.
const DOCK_GAP_PX = 12;
const EDGE_GUTTER_PX = 16;
// The one thing pinned to the foot of a page that the prompt must sit above:
// the account area's bottom nav bar (see pages/account/accountNav.jsx).
const BOTTOM_DOCK_SELECTOR = '[data-app-bottom-dock="true"]';

/**
 * T-050 (F3) — how tall the page's own pinned bottom bar is, measured.
 * 0 on every page that has none, which is most of them.
 */
function useBottomDockHeight(pathname) {
  const [height, setHeight] = useState(0);
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const node = document.querySelector(BOTTOM_DOCK_SELECTOR);
    if (!node) {
      setHeight(0);
      return undefined;
    }
    const measure = () => {
      const next = Math.round(node.getBoundingClientRect().height || 0);
      setHeight((prev) => (prev === next ? prev : next));
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      if (typeof window === "undefined") return undefined;
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
    // The bar belongs to a route, so re-look for it whenever the route moves.
  }, [pathname]);
  return height;
}

/**
 * The quiet, non-blocking "a new version is ready" toast — the visible half
 * of appUpdateWatcher.js. Mounted once at the app root (next to
 * ConfirmDialogHost), inside BrowserRouter so it can watch route changes.
 *
 * T-050 (F3) — bottom-centre, not top-centre. Top-centre sat exactly over the
 * clan workspace's board toolbar: the board switcher, "Add board", "Follow"
 * and the start of "Search cards" were all underneath it. The bottom edge is
 * free in the middle: react-hot-toast's <Toaster> owns bottom-left, the To-do
 * widget and the mini chat own bottom-right, and the account area's nav bar
 * owns the very foot of its own page. The prompt is therefore centred inside
 * the band those leave, measuring the right-hand dock through
 * `utils/workspaceDock` and the bottom bar directly, and shrinking to fit
 * rather than overlapping when the band is narrow.
 */
export default function AppUpdatePrompt() {
  const location = useLocation();
  const [watcherState, setWatcherState] = useState(() => getState());
  const [snoozedUntil, setSnoozedUntil] = useState(null);
  const dockReservedRight = useSyncExternalStore(
    subscribeDockReservedRight,
    getDockReservedRight,
    () => 0
  );
  const bottomDockHeight = useBottomDockHeight(location.pathname);

  useEffect(() => {
    start();
    return subscribe(setWatcherState);
  }, []);

  useEffect(() => {
    notifyRouteChange(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (!snoozedUntil) return undefined;
    const remaining = Math.max(snoozedUntil - Date.now(), 0);
    const timer = setTimeout(() => setSnoozedUntil(null), remaining);
    return () => clearTimeout(timer);
  }, [snoozedUntil]);

  const handleLater = useCallback(() => {
    setSnoozedUntil(Date.now() + SNOOZE_DURATION_MS);
  }, []);

  const handleReload = useCallback(() => {
    window.location.reload();
  }, []);

  const isSnoozed = Boolean(snoozedUntil) && Date.now() < snoozedUntil;
  const visible = watcherState.updateAvailable && !isSnoozed;

  if (!visible) return null;

  return (
    <div
      className={styles.prompt}
      role="status"
      aria-live="polite"
      data-testid="app-update-prompt"
      // Measured, never assumed: the To-do widget is 326px wide open and 248px
      // collapsed, moves in at 1180px and stops floating at 640px, and the
      // account bottom bar only exists on the account area.
      data-dock-reserved-right={dockReservedRight}
      data-bottom-dock-height={bottomDockHeight}
      style={{
        "--app-update-right-gutter": `${
          dockReservedRight > 0 ? dockReservedRight + DOCK_GAP_PX : EDGE_GUTTER_PX
        }px`,
        "--app-update-bottom": `${
          bottomDockHeight > 0 ? bottomDockHeight + DOCK_GAP_PX : EDGE_GUTTER_PX
        }px`,
      }}
    >
      <span className={styles.message}>A new version of Uniflo is ready.</span>
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.reloadButton}
          data-testid="app-update-reload"
          onClick={handleReload}
        >
          Reload
        </button>
        <button
          type="button"
          className={styles.laterButton}
          data-testid="app-update-later"
          onClick={handleLater}
        >
          Later
        </button>
      </div>
    </div>
  );
}
